import { useEffect, useState, useRef, type RefObject } from "react";
import { Timer as TimerIcon, Play, Square, CheckCircle } from "lucide-react";
import { apiFetch } from "../api/client";

type ActiveSession = {
  id: string;
  planned_minutes: number;
  started_at: string;
  project_id: string | null;
  task_id: string | null;
};

type Project = { id: string; name: string; color: string };
type Task = { id: string; name: string };
type Prefs = { focus_creates_entry: boolean; pomodoro_minutes: number; break_minutes: number };

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const s = (totalSeconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function Timer() {
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [prefs, setPrefs] = useState<Prefs>({
    focus_creates_entry: false,
    pomodoro_minutes: 25,
    break_minutes: 5,
  });
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const intervalRef: RefObject<ReturnType<typeof setInterval> | null> = useRef(null);

  useEffect(() => {
    async function load() {
      const [activeRes, projectsRes, prefsRes] = await Promise.all([
        apiFetch("/timer/active"),
        apiFetch("/projects"),
        apiFetch("/auth/me/preferences"),
      ]);
      if (activeRes.ok) {
        const data = await activeRes.json();
        if (data && data.id) setActiveSession(data);
      }
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (prefsRes.ok) setPrefs(await prefsRes.json());
      setLoading(false);
    }
    load();
  }, []);

  useEffect(() => {
    if (activeSession) {
      const startedAt = new Date(activeSession.started_at).getTime();
      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      }, 1000);
    } else {
      setElapsed(0);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeSession]);

  useEffect(() => {
    let cancelled = false;
    setSelectedTaskId("");
    if (selectedProjectId) {
      apiFetch(`/projects/${selectedProjectId}/tasks`).then(async (r) => {
        if (r.ok && !cancelled) setTasks(await r.json());
      });
    } else {
      setTasks([]);
    }
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  async function handleStart() {
    setMessage("");
    const body: Record<string, unknown> = { planned_minutes: prefs.pomodoro_minutes };
    if (selectedProjectId) body.project_id = selectedProjectId;
    if (selectedTaskId) body.task_id = selectedTaskId;
    const res = await apiFetch("/timer/start", {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setActiveSession(await res.json());
    } else {
      const data = await res.json().catch(() => ({}));
      setMessage((data as { detail?: string }).detail ?? "Failed to start session");
    }
  }

  async function handleStop() {
    setMessage("");
    const res = await apiFetch("/timer/stop", { method: "POST" });
    if (res.ok) {
      setActiveSession(null);
      setMessage(
        prefs.focus_creates_entry
          ? "Session complete! Time entry created."
          : "Session complete!"
      );
    } else {
      setMessage("Failed to stop session");
    }
  }

  async function toggleFocusCreatesEntry() {
    const newVal = !prefs.focus_creates_entry;
    const res = await apiFetch("/auth/me/preferences", {
      method: "PUT",
      body: JSON.stringify({ focus_creates_entry: newVal }),
    });
    if (res.ok) {
      setPrefs((p) => ({ ...p, focus_creates_entry: newVal }));
    } else {
      setMessage("Failed to update preference");
    }
  }

  const plannedSeconds = (activeSession?.planned_minutes ?? prefs.pomodoro_minutes) * 60;
  const remaining = Math.max(plannedSeconds - elapsed, 0);
  const progress = plannedSeconds > 0 ? Math.min(elapsed / plannedSeconds, 1) : 0;
  const circumference = 2 * Math.PI * 45;

  if (loading) return <div className="text-ink-2 text-sm">Loading…</div>;

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
        <TimerIcon size={22} className="text-brand" />
        Focus Timer
      </h1>

      <div className="bg-card rounded-xl p-8 flex flex-col items-center space-y-6 border border-white/[0.05]">
        <div className="relative w-48 h-48">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#1c1c21" strokeWidth="8" />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={activeSession ? "#ff6b4a" : "#1c1c21"}
              strokeWidth="8"
              strokeDasharray={`${circumference}`}
              strokeDashoffset={`${circumference * (1 - progress)}`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl font-mono font-bold text-ink">
              {formatTime(activeSession ? remaining : prefs.pomodoro_minutes * 60)}
            </span>
          </div>
        </div>

        {!activeSession && (
          <div className="w-full space-y-3">
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full bg-field border border-white/[0.08] rounded-lg pl-3 pr-8 py-2 text-sm text-ink-2 focus:outline-none focus:border-brand transition-colors"
            >
              <option value="">No project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {tasks.length > 0 && (
              <select
                value={selectedTaskId}
                onChange={(e) => setSelectedTaskId(e.target.value)}
                className="w-full bg-field border border-white/[0.08] rounded-lg pl-3 pr-8 py-2 text-sm text-ink-2 focus:outline-none focus:border-brand transition-colors"
              >
                <option value="">No task</option>
                {tasks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {activeSession ? (
          <button
            onClick={handleStop}
            className="w-full bg-red-600 hover:bg-red-500 text-white rounded-lg py-3 font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Square size={16} />
            Stop Session
          </button>
        ) : (
          <button
            onClick={handleStart}
            className="w-full bg-brand hover:bg-brand-2 text-white rounded-lg py-3 font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Play size={16} />
            Start {prefs.pomodoro_minutes}min Session
          </button>
        )}

        {message && (
          <p className={`text-sm text-center flex items-center justify-center gap-1.5 ${message.startsWith("Failed") ? "text-red-400" : "text-brand"}`}>
            {!message.startsWith("Failed") && <CheckCircle size={14} />}
            {message}
          </p>
        )}
      </div>

      <div className="bg-card rounded-xl p-4 border border-white/[0.05]">
        <label className="flex items-center justify-between cursor-pointer select-none">
          <span className="text-sm text-ink-2">Create timesheet entry on completion</span>
          <button
            type="button"
            onClick={toggleFocusCreatesEntry}
            aria-checked={prefs.focus_creates_entry}
            role="switch"
            className={`relative w-11 h-6 rounded-full transition-colors ${
              prefs.focus_creates_entry ? "bg-brand" : "bg-field"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                prefs.focus_creates_entry ? "translate-x-5" : ""
              }`}
            />
          </button>
        </label>
      </div>
    </div>
  );
}
